// Equal-width label-over-value readouts in one row; an item with a `payload` is a tap target.
// Flutter twin: metricrow.dart.
import { resetButton, toneColor } from './ui';

// `inline` (app 0.10.2+): one muted line of "label value suffix" parts; an empty value drops its part
// and the whole line taps as the first item carrying a payload.
function InlineMetrics({ source, emit }) {
  const items = (Array.isArray(source) ? source : []).filter((it) => it && typeof it === 'object');
  const parts = items.filter((it) => it.value != null && String(it.value).trim() !== '');
  if (!parts.length) return null;
  const target = items.find((it) => it.payload !== undefined);
  const line = parts.map((it, i) => (
    <span key={it.key ?? i}>
      {i > 0 ? ' · ' : ''}
      {it.label ? `${it.label} ` : ''}
      <span data-metric-value={it.key} style={it.tone ? { color: toneColor(it.tone) } : undefined}>{String(it.value).trim()}</span>
      {it.suffix ? ` ${it.suffix}` : ''}
    </span>
  ));
  const style = {
    display: 'block', width: '100%', minWidth: 0, fontSize: 12, lineHeight: 1.35, color: 'var(--text-3)',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'start',
  };
  if (!target || !emit) return <div data-metric-row="inline" style={style}>{line}</div>;
  const key = target.key != null ? String(target.key) : `m${items.indexOf(target)}`;
  const payload = target.payload;
  return (
    <button
      type="button" data-metric-row="inline"
      onClick={() => emit('activated', { key, payload, ...(payload && typeof payload === 'object' ? payload : {}) })}
      style={{ ...resetButton, ...style, cursor: 'pointer' }}
    >{line}</button>
  );
}

function itemsOf(source) {
  if (!Array.isArray(source)) return [];
  return source
    .filter((it) => it && typeof it === 'object' && (it.label != null || it.value != null))
    .map((it, i) => ({
      key: it.key != null ? String(it.key) : `m${i}`,
      label: it.label == null ? '' : String(it.label),
      value: it.value == null || it.value === '' ? '—' : String(it.value),
      tone: it.tone ? String(it.tone) : null,
      hint: it.hint == null ? '' : String(it.hint),
      payload: it.payload,
    }));
}

export function MetricRow({ variant, props = {}, data, emit }) {
  if (variant === 'inline') return <InlineMetrics source={props.items !== undefined ? props.items : data} emit={emit} />;
  const items = itemsOf(props.items !== undefined ? props.items : data);
  if (!items.length) return null;
  return (
    <div data-metric-row style={{ display: 'flex', gap: 8, minWidth: 0 }}>
      {items.map((it) => {
        const body = (
          <>
            <span style={{ fontSize: 11, color: 'var(--text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.label}</span>
            <span data-metric-value={it.key} style={{
              fontSize: 14, fontWeight: 600, color: it.tone ? toneColor(it.tone) : 'var(--text)',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>{it.value}</span>
            {it.hint ? <span data-metric-hint={it.key} style={{ fontSize: 11, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>{it.hint}</span> : null}
          </>
        );
        const box = { flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2, textAlign: 'start' };
        return it.payload !== undefined && emit ? (
          <button
            key={it.key} type="button" aria-label={`${it.label}: ${it.value}`}
            onClick={() => emit('activated', { key: it.key, payload: it.payload, ...(it.payload && typeof it.payload === 'object' ? it.payload : {}) })}
            style={{ ...resetButton, ...box, cursor: 'pointer' }}
          >{body}</button>
        ) : (
          <div key={it.key} style={box}>{body}</div>
        );
      })}
    </div>
  );
}
